import { api } from './api';

/* Mirrors Eduvora.Application.Eligibility.
   ⚠ No money field anywhere — a class teacher can reach the first endpoint. */

export type Band = 'Eligible' | 'Condonation' | 'Detained' | 'NotApplicable';

export interface EligibilityStudent {
  studentId: string;
  fullName: string;
  admissionNumber: string;
  classKey: string;
  classLabel: string;
  held: number;
  attended: number;
  /** ⚠ null means no attendance was marked at all — NOT 0%. Render "—". */
  percent: number | null;
  band: Band;
  /** Consecutive present days needed, from today, to reach the threshold. */
  daysNeeded: number;
  teachingDaysLeft: number;
  /** False = cannot reach the threshold before the cut-off even with perfect attendance. */
  canStillRecover: boolean;
  verdict: string;
  missedExams: number;
  /** VTU only: subject codes where the CIE is below the minimum to sit the SEE. */
  cieShortfallSubjects: string[];
}

export interface EligibilityClass {
  classKey: string;
  classLabel: string;
  strength: number;
  eligible: number;
  condonation: number;
  detained: number;
  unrecoverable: number;
  averagePercent: number | null;
  classTeacherId: string | null;
  classTeacherName: string | null;
  classTeacherDesignation: string | null;
  /** ⚠ null unless the caller is in the `hr` policy — a staff personal number is not
      staffroom-wide information. Server-side redaction, not a UI choice. */
  classTeacherMobile: string | null;
  classTeacherOnLeaveToday: boolean;
}

export interface EligibilityInstitution {
  id: string;
  name: string;
  kind: string;
  board: string;
}

export interface ExamEligibility {
  institutionId: string | null;
  institutions: EligibilityInstitution[];
  ruleLabel: string;
  eligibleThreshold: number;
  condonationFloor: number;
  minCiePercent: number;
  from: string;
  to: string;
  cutOff: string;
  cutOffLabel: string;
  excludeExamDays: boolean;
  examDaysExcluded: number;
  teachingDaysLeft: number;
  usesPeriodAttendance: boolean;

  students: number;
  eligible: number;
  condonation: number;
  detained: number;
  unrecoverable: number;
  notApplicable: number;
  cieShortfallStudents: number;

  classes: EligibilityClass[];
  watchlist: EligibilityStudent[];
  note: string | null;
}

export interface EligibilityQuery {
  institutionId?: string | null;
  from?: string | null;
  to?: string | null;
  cutOff?: string | null;
  excludeExamDays?: boolean;
  minCie?: number | null;
  /** Ask the leadership route, which adds the class teacher's mobile. Only send true for a role
      actually in the `hr` policy, or it comes back 403. */
  withContact?: boolean;
}

export const eligibilityApi = {
  watchlist: (q: EligibilityQuery = {}) => {
    const p = new URLSearchParams();
    if (q.institutionId) p.set('institutionId', q.institutionId);
    if (q.from) p.set('from', q.from);
    if (q.to) p.set('to', q.to);
    if (q.cutOff) p.set('cutOff', q.cutOff);
    if (q.excludeExamDays === false) p.set('excludeExamDays', 'false');
    if (q.minCie) p.set('minCie', String(q.minCie));
    const qs = p.toString();
    const path = q.withContact ? '/api/eligibility/watchlist/leadership' : '/api/eligibility/watchlist';
    return api.get<ExamEligibility>(`${path}${qs ? '?' + qs : ''}`);
  },
};

export const BAND_STYLE: Record<Band, { label: string; cls: string }> = {
  Eligible: { label: 'Clear', cls: 'pill-ok' },
  Condonation: { label: 'Needs condonation', cls: 'pill-warn' },
  Detained: { label: 'Below the floor', cls: 'pill-danger' },
  NotApplicable: { label: 'No data', cls: 'pill-muted' },
};

/** "3 Sep" — short, because it appears inline in sentences. */
export function shortDate(iso: string): string {
  const d = new Date(iso + 'T00:00:00');
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
}
